/**
 * OtpInput — six single-digit boxes with paste support, keyboard navigation,
 * and backspace-to-previous. Preserves the prototype's a11y pattern.
 */
import { useRef, useCallback, type KeyboardEvent, type ClipboardEvent } from 'react';
import './OtpInput.css';

interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
}

export function OtpInput({
  length = 6,
  value,
  onChange,
  error = false,
  disabled = false,
  'aria-label': ariaLabel = 'One-time passcode',
}: OtpInputProps) {
  const boxesRef = useRef<Array<HTMLInputElement | null>>([]);

  const digits = value.padEnd(length, ' ').slice(0, length).split('');

  const setDigit = useCallback(
    (index: number, digit: string) => {
      const next = value.padEnd(length, ' ').slice(0, length).split('');
      next[index] = digit;
      onChange(next.join('').replace(/\s/g, ''));
    },
    [value, onChange, length],
  );

  const focusBox = (index: number) => {
    const box = boxesRef.current[index];
    if (box) {
      box.focus();
      box.select();
    }
  };

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    if (digit) {
      setDigit(index, digit);
      if (index < length - 1) focusBox(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index]?.trim() && index > 0) {
      focusBox(index - 1);
    }
    if (e.key === 'ArrowLeft' && index > 0) focusBox(index - 1);
    if (e.key === 'ArrowRight' && index < length - 1) focusBox(index + 1);
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>, startIndex: number) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length - startIndex);
    if (!pasted) return;
    const next = value.padEnd(length, ' ').slice(0, length).split('');
    for (let i = 0; i < pasted.length; i++) {
      next[startIndex + i] = pasted[i] ?? ' ';
    }
    onChange(next.join('').replace(/\s/g, ''));
    const focusIndex = Math.min(startIndex + pasted.length, length - 1);
    focusBox(focusIndex);
  };

  const classes = ['itk-otp', error ? 'itk-otp--err' : ''].filter(Boolean).join(' ');

  return (
    <div className={classes} role="group" aria-label={ariaLabel}>
      {Array.from({ length }, (_, i) => {
        const digit = digits[i] ?? ' ';
        return (
          <input
            key={i}
            ref={(el) => {
              boxesRef.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            className={`itk-otp__box ${digit.trim() ? 'itk-otp__box--filled' : ''}`}
            value={digit.trim()}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={(e) => handlePaste(e, i)}
            onFocus={(e) => e.target.select()}
            disabled={disabled}
            aria-label={`Digit ${i + 1} of ${length}`}
          />
        );
      })}
    </div>
  );
}